import { useEffect } from 'react';
import { useAtom, type PrimitiveAtom } from 'jotai';

/**
 * Persists the active tab for a page across sidebar navigation.
 *
 * URL hash takes precedence (direct links, browser back/forward). When the hash
 * is absent (e.g. the user clicked the sidebar nav item), the last-saved atom
 * value is used and the URL is immediately replaced so the address bar stays in sync.
 *
 * @param tabAtom       Jotai atom storing the last-visited tab ID.
 * @param validTabs     Tabs valid in the current render (may be filtered by test mode etc.).
 * @param defaultTab    Fallback when neither hash nor atom yields a valid tab.
 * @param navigateToTab Caller-supplied navigate fn; receives the tab ID and an optional
 *                      `replace` flag (true on the initial URL sync, false on user clicks).
 * @param currentHash   `location.hash` from the current route (with the leading `#`).
 */
export function useTabPersistence<T extends string>(
  tabAtom: PrimitiveAtom<string>,
  validTabs: readonly T[],
  defaultTab: T,
  navigateToTab: (tabId: T, replace?: boolean) => void,
  currentHash: string | undefined,
) {
  const [savedTab, setSavedTab] = useAtom(tabAtom);
  const hash = currentHash?.replace('#', '') || savedTab || defaultTab;
  const activeTab = validTabs.includes(hash as T) ? (hash as T) : defaultTab;

  // On mount: if no hash is present (sidebar nav click), replace the URL with the active tab.
  useEffect(() => {
    if (!currentHash) {
      navigateToTab(activeTab, true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (tabId: T) => {
    setSavedTab(tabId);
    navigateToTab(tabId);
  };

  return { activeTab, handleTabChange };
}
